/**
 * List + remove + test the tenant's connected integrations. Talks to
 * sc-platform's `/api/integrations/connected` endpoint.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type {
  TenantIntegrationListResponse,
  TenantIntegrationTestResult,
} from '../../lib/types.js';

interface IntegrationsListLogic {
  isLoading: boolean;
  data: TenantIntegrationListResponse | undefined;
  error: Error | null;
  refetch: () => void;
  /**
   * Keyed by integration ID, not catalog key: a tenant can hold several
   * instances of one key (two Custom API connections), so a key no longer
   * identifies which row is busy.
   */
  reconnectingId: string | null;
  reconnect: (integrationId: string) => Promise<void>;
  removingId: string | null;
  remove: (integrationId: string) => Promise<void>;
  /** Permanent: deletes the row and its secrets. Only for a disconnected connection. */
  deletingId: string | null;
  deletePermanently: (integrationId: string) => Promise<void>;
  testingId: string | null;
  testResult: { integrationId: string; result: TenantIntegrationTestResult } | null;
  test: (integrationId: string) => Promise<void>;
}

export const useIntegrationsListLogic = (tenantId: string | undefined): IntegrationsListLogic => {
  const queryClient = useQueryClient();

  const listQuery = useQuery<TenantIntegrationListResponse>({
    queryKey: queryKeys.integrations(tenantId ?? ''),
    queryFn: () => apiRequest<TenantIntegrationListResponse>(`/api/integrations/connected`),
    enabled: Boolean(tenantId),
  });

  const refreshConnections = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.integrations(tenantId ?? '') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.capabilities(tenantId ?? '') });
    void queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
  };
  const reconnectMutation = useMutation({
    mutationFn: (integrationId: string) =>
      apiRequest(`/api/integrations/${integrationId}`, {
        method: 'PUT',
        body: { fields: {}, isConnected: true },
      }),
    onSuccess: refreshConnections,
  });
  const removeMutation = useMutation<void, Error, string>({
    mutationFn: (integrationId) =>
      apiRequest<void>(`/api/integrations/${integrationId}`, {
        method: 'DELETE',
      }),
    onSuccess: refreshConnections,
  });

  const deleteMutation = useMutation<void, Error, string>({
    mutationFn: (integrationId) =>
      apiRequest<void>(`/api/integrations/${integrationId}/permanent`, { method: 'DELETE' }),
    onSuccess: refreshConnections,
  });

  const testMutation = useMutation<
    { integrationId: string; result: TenantIntegrationTestResult },
    Error,
    string
  >({
    mutationFn: async (integrationId) => {
      const result = await apiRequest<TenantIntegrationTestResult>(
        `/api/integrations/${integrationId}/test`,
        { method: 'POST' },
      );
      return { integrationId, result };
    },
    onSuccess: refreshConnections,
  });

  return {
    isLoading: listQuery.isLoading,
    data: listQuery.data,
    error:
      listQuery.error ?? removeMutation.error ?? reconnectMutation.error ?? deleteMutation.error,
    refetch: () => void listQuery.refetch(),
    reconnectingId: reconnectMutation.isPending ? reconnectMutation.variables : null,
    reconnect: async (integrationId) => {
      await reconnectMutation.mutateAsync(integrationId).catch(() => undefined);
    },
    removingId: removeMutation.isPending ? (removeMutation.variables ?? null) : null,
    remove: async (integrationId) => {
      await removeMutation.mutateAsync(integrationId).catch(() => undefined);
    },
    deletingId: deleteMutation.isPending ? (deleteMutation.variables ?? null) : null,
    deletePermanently: async (integrationId) => {
      await deleteMutation.mutateAsync(integrationId).catch(() => undefined);
    },
    testingId: testMutation.isPending ? (testMutation.variables ?? null) : null,
    testResult: testMutation.data ?? null,
    test: async (integrationId) => {
      await testMutation.mutateAsync(integrationId);
    },
  };
};
